import { ColumnDef, SortingFn, Table } from '@tanstack/react-table';
import { BaseCell } from 'src/components/TableV2/cells';
import { LinkCell } from 'src/components/TableV2/cells/LinkCell';
import {
  COLUMN_MAX_SIZE,
  COLUMN_MIN_SIZE,
} from 'src/contexts/ColumnResizingContext/ColumnResizingContext.types';
import { Content } from 'src/contexts/ContentContext';
import { TableHeaderCellDiv } from 'src/core/ui/TableHeaderCellDiv';
import { TableShimmeringDiv } from 'src/tables/Table';
import { SectionType } from 'src/utils/types/sectionType';

import { SHIMMERING_DIV_HEIGHT_SINGLE_SALE } from '../../SalesTable/SalesTable.type';
import { linkTextSearchFilter } from '../filters/filterFunctions';

interface LinkColumnOptions<TData> {
  /** Column ID */
  id: string;
  /** Content ID for the header label */
  headerContentId: string;
  /** Accessor function to get the link URL from data */
  linkAccessor: (data: TData) => string | null | undefined;
  /** Accessor function to get the display text from data */
  textAccessor: (data: TData) => string | null | undefined;
  /** Right side component to render inside the cell */
  rightComponentRender?: (data: TData, table: Table<TData>) => React.ReactNode;
  /** Column size */
  size?: number;
  /** Section type */
  sectionType?: SectionType;
  /** Show text only without icon */
  textOnly?: boolean;
  /** Custom sorting function */
  sortingFn?: SortingFn<TData>;
  /** Function to determine if cell should be rendered (return null if false) */
  shouldRenderCell?: (data: TData, table: Table<TData>) => boolean;
  /** Custom onClick handler for the link */
  onLinkClick?: (data: TData) => void;
  /** Function to determine shimmer height based on data */
  getShimmerHeight?: (data: TData) => number;
  /** Whether sorting is enabled for this column */
  enableSorting?: boolean;
}

/**
 * Factory function to create link column definitions for tables.
 * Creates a column with copyable text and a clickable link with icon.
 *
 * @example
 * createLinkColumn({
 *   id: SalesTableColumnId.Sale,
 *   headerContentId: SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.Sale],
 *   linkAccessor: (data) => getSaleDetailsRelativeUrl(data?.sale?.id),
 *   textAccessor: (data) => data?.sale?.idOnMkp || String(data?.sale?.id),
 *   size: COLUMN_DEFAULT_SIZE_XL,
 * })
 */
export const createLinkColumn = <TData,>(
  options: LinkColumnOptions<TData>
): ColumnDef<TData> => {
  const {
    id,
    headerContentId,
    linkAccessor,
    textAccessor,
    size,
    textOnly = false,
    sortingFn,
    shouldRenderCell,
    rightComponentRender,
    getShimmerHeight,
    enableSorting,
  } = options;

  return {
    id,
    enableColumnFilter: true,
    size,
    ...(enableSorting !== undefined && { enableSorting }),
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    header: () => <Content id={headerContentId} />,
    filterFn: linkTextSearchFilter,
    meta: {
      columnFilterVariant: 'textSearch',
    },
    accessorFn: (data) => ({
      text: textAccessor(data),
      link: linkAccessor(data),
    }),
    cell: function Cell({ getValue, row: { original }, table }) {
      const value = getValue<{
        text: string | null | undefined;
        link: string | null | undefined;
      }>();

      if (!original) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      // Check if cell should be rendered
      if (shouldRenderCell && !shouldRenderCell(original, table)) {
        return null;
      }

      if (!value?.text || !value?.link) {
        return <BaseCell />;
      }

      const RightComponent = rightComponentRender
        ? rightComponentRender(original, table)
        : undefined;

      return (
        <LinkCell
          text={value.text}
          link={value.link}
          textOnly={textOnly}
          rightComponent={RightComponent}
        />
      );
    },
    ...(sortingFn && { sortingFn }),
  };
};
